import {
  normalizeLanguageId as normalizeLanguageIdShared,
  normalizeProductScopeValue as normalizeProductScopeValueShared,
} from "./scope-catalog.js";

export const CURRENT_SESSION_STORAGE_KEY = "help.aiChat";

export type SessionHistoryStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
};

export type SessionHistoryContext = {
  product?: unknown;
  languageId?: unknown;
  culture?: unknown;
  activation?: unknown;
  url?: unknown;
  title?: unknown;
  query?: unknown;
  entryPoint?: unknown;
  selectedSourceUrl?: unknown;
  sourceTitle?: unknown;
  kbId?: unknown;
  filters?: unknown;
  [key: string]: unknown;
};

export type WorkspaceScope = {
  product: string;
  culture: string;
  languageId: number;
  activation: string;
};

// Deliberately holds no conversation content: only the handle to the chat and the scope it
// belongs to. The messages are fetched back from /api/chat/resume, so a shared or stolen
// browser profile yields nothing readable without the visitor's own chat cookie.
export type CurrentSessionSnapshot = {
  sessionToken: string;
  sessionScope: SessionHistoryContext | Partial<WorkspaceScope>;
};

export type SessionHistoryStoreOptions = {
  sessionStorage: SessionHistoryStorage;
};

export type SessionHistoryStore = {
  readCurrentSession(): CurrentSessionSnapshot | null;
  writeCurrentSession(snapshot: CurrentSessionSnapshot): void;
  clearCurrentSession(): void;
};

export function createSessionHistoryStore(
  options: SessionHistoryStoreOptions,
): SessionHistoryStore {
  return {
    readCurrentSession(): CurrentSessionSnapshot | null {
      return readCurrentSession(options.sessionStorage);
    },
    writeCurrentSession(snapshot: CurrentSessionSnapshot): void {
      writeCurrentSession(options.sessionStorage, snapshot);
    },
    clearCurrentSession(): void {
      clearCurrentSession(options.sessionStorage);
    },
  };
}

export function readCurrentSession(
  storage: SessionHistoryStorage,
): CurrentSessionSnapshot | null {
  const parsed = parseJson(storage.getItem(CURRENT_SESSION_STORAGE_KEY));
  if (!isRecord(parsed)) return null;

  const sessionToken = stringValue(parsed.sessionToken);
  if (!sessionToken) return null;

  const snapshot: CurrentSessionSnapshot = {
    sessionToken,
    sessionScope: isRecord(parsed.sessionScope) ? { ...parsed.sessionScope } : {},
  };

  // A snapshot written by an older build also carries the conversation itself. Reading past
  // those fields is not enough: if this load never writes back (resume fails, the platform
  // is unreachable, the tab is closed) the old messages stay readable in sessionStorage,
  // which is the finding this key was reshaped for. Replace it here, at first sight.
  if ("messages" in parsed || "context" in parsed) {
    writeCurrentSession(storage, snapshot);
  }

  return snapshot;
}

export function writeCurrentSession(
  storage: SessionHistoryStorage,
  snapshot: CurrentSessionSnapshot,
): void {
  safeSetItem(
    storage,
    CURRENT_SESSION_STORAGE_KEY,
    JSON.stringify({
      sessionToken: snapshot.sessionToken,
      sessionScope: snapshot.sessionScope,
    }),
  );
}

export function clearCurrentSession(storage: SessionHistoryStorage): void {
  try {
    if (storage.removeItem) storage.removeItem(CURRENT_SESSION_STORAGE_KEY);
    else storage.setItem(CURRENT_SESSION_STORAGE_KEY, "");
  } catch {
    // Storage can be unavailable in privacy modes; persistence is best-effort.
  }
}

export function getWorkspaceScope(
  context: SessionHistoryContext | Partial<WorkspaceScope> | null | undefined,
): WorkspaceScope {
  const source = context ?? {};
  return {
    product: normalizeProductScopeValue(source.product || "se"),
    culture: normalizeScopeValue(source.culture || "nl"),
    languageId: normalizeLanguageId(source.languageId, source.culture),
    activation: normalizeScopeValue(source.activation || ""),
  };
}

export function scopeMatches(
  left: SessionHistoryContext | Partial<WorkspaceScope>,
  right: SessionHistoryContext | Partial<WorkspaceScope>,
): boolean {
  const leftScope = getWorkspaceScope(left);
  const rightScope = getWorkspaceScope(right);
  return (
    leftScope.product === rightScope.product &&
    leftScope.culture === rightScope.culture &&
    leftScope.languageId === rightScope.languageId &&
    optionalScopeValueMatches(leftScope.activation, rightScope.activation)
  );
}

// Compares two scopes on product and activation (country) only, ignoring culture and
// languageId. Use this when a match must hold regardless of language.
export function scopeMatchesIgnoringLanguage(
  left: SessionHistoryContext | Partial<WorkspaceScope>,
  right: SessionHistoryContext | Partial<WorkspaceScope>,
): boolean {
  const leftScope = getWorkspaceScope(left);
  const rightScope = getWorkspaceScope(right);
  return (
    leftScope.product === rightScope.product &&
    optionalScopeValueMatches(leftScope.activation, rightScope.activation)
  );
}

function optionalScopeValueMatches(left: string, right: string): boolean {
  return !left || !right || left === right;
}

export function normalizeScopeValue(value: unknown): string {
  return normalizeQuestionText(value).toLowerCase();
}

export function normalizeProductScopeValue(value: unknown): string {
  return normalizeProductScopeValueShared(String(value ?? ""));
}

export function normalizeLanguageId(
  languageId: unknown,
  culture?: unknown,
): number {
  return normalizeLanguageIdShared(
    String(languageId ?? ""),
    String(culture ?? ""),
  );
}

export function normalizeQuestionText(value: unknown): string {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();
}

function parseJson(value: string | null): unknown {
  if (!value) return null;
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
}

function safeSetItem(
  storage: SessionHistoryStorage,
  key: string,
  value: string,
): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Storage can be unavailable in privacy modes; persistence is best-effort.
  }
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value : value == null ? "" : String(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
